/**
 * What happened to a database, and how to ask for a narrower answer.
 *
 * The activity history is the database-wide counterpart of the panel beside a
 * row: the same entries, unfiltered by which row they belong to. It exists
 * because the row panel cannot answer the question this product is most often
 * asked -- *what did the agent just do* -- when the answer spans forty rows
 * across three tables and nobody knows which rows to go and look at.
 *
 * Two shapes come back for that. The **grouped** endpoint collapses every entry
 * sharing an `action_uuid` into one line: one API or MCP call, however many
 * rows it touched. The **flat** endpoint, asked for a single `action_uuid`, is
 * what that line expands into. So the list is a list of operations and not of
 * rows, which is the only way a session that wrote two thousand rows is
 * readable at all.
 *
 * Everything here is pure. The rendering of an individual change is not here
 * either: it is `features/database/row/history`, unchanged, because a change is
 * a change whichever list it is read in.
 */
import type {
  DatabaseRowHistory,
  DatabaseRowHistoryGroup,
  RevertOutcome,
} from '@/api/types'
import type { HistoryEntryOf } from '../row/history'
import { dayHeading, type DayHeading } from '@/shared/lib/when'

/**
 * One entry of the activity history, ready to render.
 *
 * The row panel's entry plus which table and row it belongs to, where it came
 * in through, and the two ids a revert is addressed by. Assignable to
 * `HistoryEntry`, which is what lets `historyChanges` read it unchanged.
 */
export type ActivityEntry = HistoryEntryOf<DatabaseRowHistory>

/**
 * Where a change came in through.
 *
 * `system` is the odd one: the backend stores an empty string for a write that
 * had no request behind it -- a migration, a scheduled task, a field default
 * applied on a type change -- and accepts the word `system` as the way to ask
 * for those. So the value on an entry and the value in a query are different
 * for exactly one of the four, and `sourceOf` is where that is known.
 */
export const ACTIVITY_SOURCES = ['web', 'api_token', 'mcp', 'system'] as const

export type ActivitySource = (typeof ACTIVITY_SOURCES)[number]

/**
 * The source an entry records, as one of the four the filter offers.
 *
 * `null` for anything else, which is a real answer rather than a fallback: a
 * source this build has never heard of is still a record of something, and
 * calling it `system` would be a guess about where it came from. The list names
 * it as it stands instead -- the same rule the notification panel follows for a
 * type it does not know.
 */
export function sourceOf(source: string): ActivitySource | null {
  if (source === '') return 'system'
  return (ACTIVITY_SOURCES as readonly string[]).includes(source)
    ? (source as ActivitySource)
    : null
}

/** The three kinds of change the endpoint will narrow to. */
export const ACTIVITY_CATEGORIES = ['created', 'updated', 'deleted'] as const

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number]

/**
 * What the reader has narrowed the list to.
 *
 * The two dates are calendar days as a date input gives them -- `YYYY-MM-DD` --
 * rather than instants, because that is what is being asked: "since Monday",
 * not "since 00:00:00.000Z on Monday". Turning them into instants is
 * `activityQueryParams`'s job and is where the reader's own timezone comes in.
 */
export interface ActivityFilters {
  source?: ActivitySource
  userId?: number
  tableId?: number
  category?: ActivityCategory
  from?: string
  to?: string
}

/** Whether anything has been narrowed, which decides whether to offer a reset. */
export function hasActivityFilters(filters: ActivityFilters): boolean {
  return Object.values(filters).some((value) => value !== undefined)
}

/** The start of a local calendar day, as the instant the endpoint wants. */
function startOfDay(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString()
}

/**
 * The *end* of a local calendar day, to the millisecond.
 *
 * `to_timestamp` is inclusive -- "at or before this time" -- so a day passed as
 * its own midnight would exclude everything that happened during it. Filtering
 * "up to today" and seeing nothing from today is the one way to be wrong here
 * that reads as the filter being broken rather than as it being strict.
 */
function endOfDay(day: string): string {
  return new Date(`${day}T23:59:59.999`).toISOString()
}

/** The query the endpoint takes, with nothing said about what was not narrowed. */
export function activityQueryParams(filters: ActivityFilters): Record<string, unknown> {
  return {
    ...(filters.source !== undefined && { source: filters.source }),
    ...(filters.userId !== undefined && { user_id: filters.userId }),
    ...(filters.tableId !== undefined && { table_id: filters.tableId }),
    ...(filters.category !== undefined && { action_category: filters.category }),
    ...(filters.from !== undefined && { from_timestamp: startOfDay(filters.from) }),
    ...(filters.to !== undefined && { to_timestamp: endOfDay(filters.to) }),
  }
}

/**
 * The operations under a heading for the day they happened on.
 *
 * Consecutive rather than sorted, because the endpoint answers newest first and
 * the sections inherit that: an operation whose day differs from the one before
 * it starts a new section. Sorting here would be inventing an order over data
 * that already has one, and would disagree with the order the next page arrives
 * in.
 */
export interface DaySection {
  heading: DayHeading
  operations: DatabaseRowHistoryGroup[]
}

export function daySections(
  operations: readonly DatabaseRowHistoryGroup[],
  now: Date,
): DaySection[] {
  const sections: DaySection[] = []

  for (const operation of operations) {
    const heading = dayHeading(operation.timestamp, now)
    const last = sections[sections.length - 1]
    if (last !== undefined && last.heading.key === heading.key) {
      last.operations.push(operation)
    } else {
      sections.push({ heading, operations: [operation] })
    }
  }

  return sections
}

/**
 * What a revert managed, as the two numbers worth saying out loud.
 *
 * Taken from the outcomes rather than from the counts beside them, which is not
 * distrust of the server: the outcomes are what the dialog lists, and a summary
 * counted from a different source than the list it heads can disagree with it.
 * The server's own totals are asserted against these in the spec instead, which
 * is where a disagreement should surface.
 */
export interface RevertTotals {
  reverted: number
  skipped: number
  /** The reasons rows were skipped, each with how many, most common first. */
  reasons: { status: string; count: number }[]
}

export function revertTotals(outcomes: readonly RevertOutcome[]): RevertTotals {
  const skipped = outcomes.filter((outcome) => outcome.status.startsWith('skipped'))
  const byReason = new Map<string, number>()
  for (const outcome of skipped) {
    byReason.set(outcome.status, (byReason.get(outcome.status) ?? 0) + 1)
  }

  return {
    reverted: outcomes.length - skipped.length,
    skipped: skipped.length,
    reasons: [...byReason]
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count || a.status.localeCompare(b.status)),
  }
}
